import React, { useEffect } from 'react'
import { useApi } from '@hooks/use-api-requests.hook.ts'
import { useAppContext } from '@hooks/use-app-context.ts'
import { Actions, Steps } from '@store/reducer.ts'
import {
	getError,
	isEXTAllowed,
	isNameOverLimit,
	isOverWeight,
	UPLOAD_ERROR_TEMPLATES,
} from '@utils/validators.ts'
//import { OCRDocument, useOCR, UserDocument } from '@hooks/use-ocr.hooks.ts'
import { useModal } from '@hooks/use-modal.ts'
import { log } from '@utils/debug'
import type { UserDocument } from '@hooks/type-ds'
import { useOCR } from '@hooks/use-ocr.hooks'
//import { currentStepAtom } from '@store/store'
import { useAtom } from 'jotai'

import { StepsRichiesta } from '@store/stepsRichiesta'
import { currentStepAtom } from '@store/atoms'

type UploadError = {
	type:
		| 'SIZE'
		| 'GENERIC'
		| 'NOT_SUPPORTED'
		| 'NONE'
		| 'WRONG_DOCUMENT'
		| 'CONFIDENCE_LOW'
		| 'OCR'
		| 'DOCUMENT_EXPIRED'
		| 'DOCUMENT_INVALID'
		| 'EXCEEDED_LIMIT'
		| 'INVALID_DOCUMENT'
		| 'NAME_INVALID'
	title: string
	description: string
}

type SaveDocument = {
	DocumentsList: Array<{
		Name: string
		Type: string
		Data: string
		DocumentType: string | null
	}>
	idRichiesta: number
	ResultNumber: number
}

export function _useReset(
	setError: React.Dispatch<React.SetStateAction<UploadError>>,
	setFurther?: React.Dispatch<React.SetStateAction<boolean>>,
	shouldFurther = false
) {
	return () => {
		setFurther?.(shouldFurther)
		setError(getError('NONE'))
	}
}

function useUpload(
	id: UserDocument,
	maxDocs = 2,
	minDocs = 2,
	onUpload?: (n: {
		status: boolean
		isFilled: boolean
		shouldUpload: boolean
		id: UserDocument
		hasBeenUploaded: boolean
	}) => void
) {
	const [activeStep] = useAtom(currentStepAtom) //STEP PADRE
	const { state, dispatch } = useAppContext()
	const { fetchOCR, completeStatus, error, setComplete } = useOCR(id)
	const { goTo, close } = useModal()

	const [files, setFiles] = React.useState<Array<File>>(state.files[id] ?? [])
	const [showLoader, setLoader] = React.useState(false)
	const [canGoFurther, setFurther] = React.useState(false)
	const [hasError, setError] = React.useState<UploadError>({
		type: 'NONE',
		description: UPLOAD_ERROR_TEMPLATES['GENERIC'].description,
		title: UPLOAD_ERROR_TEMPLATES['GENERIC'].title,
	})

	const shouldUseOcr =
		activeStep === StepsRichiesta.CONTATTI && (id === 'IDE' || id === 'CF')

	const [uploadedFiles, uploadAction, uploadingFiles] = useApi<SaveDocument>({
		request: 'DbOperations/SaveDocuments',
		method: 'POST',
		sleeper: true,
		silent: true,
		file: true,
		shouldCache: false,
	})

	const resetFiles = _useReset(setError, setFurther, files.length >= minDocs)

	/**
	 * A function that throws a limit error and updates the files.
	 */
	const throwLimitError = () => {
		//setError(getError('EXCEEDED_LIMIT'))
	}

	/**
	 * Updates the list of files based on the provided fileList.
	 *
	 * @param {FileList | null} fileList - The list of files to be updated
	 * @return {void}
	 */

	const updateFiles = (input: File | FileList | null) => {
		if (!input) return

		const incoming: File[] = input instanceof File ? [input] : Array.from(input)

		setFiles((prev) => {
			// usa prev, NON files
			if (incoming.length + prev.length > maxDocs) {
				//	throwLimitError()
				//	return prev
			}

			const filterExistingFiles = incoming.filter(
				(e) => !prev.find((x) => x.name === e.name)
			)

			const checked = filterExistingFiles
				.map((file) => {
					const fileOverLimit = isOverWeight(file)
					const invalidExts = !isEXTAllowed(file)
					const fileNameOverLimit = isNameOverLimit(file, 100)

					if (fileOverLimit) setError(getError('SIZE'))
					else if (invalidExts) setError(getError('NOT_SUPPORTED'))
					else if (fileNameOverLimit) setError(getError('NAME_INVALID'))
					else {
						setError(getError('NONE'))
						return file
					}
					return null
				})
				.filter((f): f is File => f !== null)

			const next = [...prev, ...checked]

			// ✅ sincronizza lo store con lo stesso "next"
			dispatch({
				type: Actions.UpdateFiles,
				payload: { name: id, files: next },
			})

			return next
		})
	}

	/* const updateFiles = (fileList: FileList | null) => {
		if (fileList) {
			if (fileList.length + files.length > maxDocs) {
				return throwLimitError()
			}
			const fileListArray = Array.from(fileList)

			const filterExistingFiles = fileListArray.filter(
				(e) => !files.find((x) => x.name === e.name)
			)

			const checked = filterExistingFiles
				.map((file) => {
					const fileOverLimit = isOverWeight(file)
					const invalidExts = !isEXTAllowed(file)
					const fileNameOverLimit = isNameOverLimit(file, 100)

					if (fileOverLimit) {
						setError(getError('SIZE'))
					} else if (invalidExts) {
						setError(getError('NOT_SUPPORTED'))
					} else if (fileNameOverLimit) {
						setError(getError('NAME_INVALID'))
					} else {
						setError(getError('NONE'))

						return file
					}

					return null
				})
				.filter((file) => file !== null) as Array<File>

			setFiles([...files, ...checked])

			dispatch({
				type: Actions.UpdateFiles,
				payload: {
					name: id,
					files: [...files, ...checked],
				},
			})
		}
	} */

	/**
	 * This function asynchronously creates a FormData object and appends files and other data to it.
	 *
	 * @return {FormData} The FormData object containing the files and additional data.
	 */
	const getFormData = async () => {
		const formData = new FormData()
		if (files) {
			for await (const file of files) {
				formData.append(file.name, file)
			}

			formData.append('idRichiesta', String(state.data.IdRichiesta))
			formData.append('idSessione', state.data.sessione?.toString() ?? '')
			formData.append('DocumentType', id)
			formData.append('isCIE', state.ui.isCIE.toString())

			console.log(
				'FILES TO SEND',
				files.map((f) => ({
					name: f.name,
					size: f.size,
					type: f.type,
				}))
			)

			return formData
		}
	}

	const uploadFiles = async () => {
		setLoader(true)

		const formData = await getFormData()
		if (!formData) {
			setLoader(false)
			return
		}

		if (shouldUseOcr) {
			fetchOCR({ body: formData })
			return
		}

		log('form-data', formData)
		uploadAction({ body: formData })
	}

	/* const uploadFiles = async () => {
		setLoader(true)
		// we only want to trigger the OCR call when in RICHIESTA
		const formData = await getFormData()
		if (activeStep === StepsRichiesta.CONTATTI) {
			fetchOCR({ body: formData })
		} else {
			log('form-data', formData)
			uploadAction({ body: formData })
		}
	} */

	const saveDocuments = async () => {
		// non salvare mentre stai caricando o se c'è errore
		if (showLoader) return
		if (hasError.type !== 'NONE') return

		const formData = await getFormData()
		if (!formData) return

		uploadAction({ body: formData })
	}

	const removeFile = (name: string) => {
		const filteredFiles = files?.filter((file) => file.name !== name)

		setFiles(filteredFiles)

		if (state.files[id]) {
			dispatch({
				type: Actions.UpdateFiles,
				payload: {
					name: id,
					files: filteredFiles,
				},
			})
		}
	}

	const closeModal = () => {
		resetFiles()
		close()
	}

	/* React.useEffect(() => {
		if (completeStatus !== null) {
			if (completeStatus && hasError.type === 'NONE') {
				getFormData().then((formData) => {
					if (formData) {
						uploadAction({ body: formData })
					} else {
						console.error('cannot send document, formData is empty')
					}
				})
			}
		}
	}, [completeStatus]) */

	React.useEffect(() => {
		setComplete(null)
		setLoader(false)
		setError(error)
	}, [error])

	React.useEffect(() => {
		if (uploadedFiles && files && uploadedFiles.DocumentsList?.length > 0) {
			onUpload?.({
				status: true,
				isFilled: files.length >= minDocs,
				shouldUpload: false,
				hasBeenUploaded: false,
				id,
			})

			dispatch({
				type: Actions.UpdateFiles,
				payload: {
					name: id,
					files: files,
				},
			})

			if (
				activeStep === StepsRichiesta.CONTATTI ||
				activeStep === Steps.RIEPILOGO
			) {
				setLoader(false)
				goTo('NEXT')
			}
		} else if (uploadedFiles) {
			if (uploadedFiles.ResultNumber !== 0) {
				setError(getError('NAME_INVALID'))
			} else {
				setError(getError('GENERIC'))
			}
			setLoader(false)
		}
	}, [uploadedFiles])

	useEffect(() => {
		const len = files?.length ?? 0
		setFurther(len >= minDocs && len <= maxDocs)

		if (files.length > maxDocs) {
			throwLimitError()
		}
	}, [files])

	// check to see if an IDE with fiscal code has already been uploaded
	// if that's the case let's skip the upload if its a CF
	useEffect(() => {
		if (
			state.ui.isCIE &&
			id === 'CF' &&
			activeStep === StepsRichiesta.CONTATTI
		) {
			goTo('NEXT')
		}
	}, [])

	return {
		files,
		updateFiles,
		uploadFiles,
		uploadedFiles,
		removeFile,
		showLoader,
		uploadingFiles,
		hasError,
		resetFiles,
		canGoFurther,
		closeModal,
		saveDocuments,
		completeStatus,
	}
}

export { useUpload, type UploadError, type OCRDocument }
